import { useCallback, useState } from "react";
import { Values } from "@/constants";

export type RestApiCallbacks<T> = {
  onSuccess?: (data: T) => void;
  onError?: (error: string) => void;
};

export type RestApiOptions<T> = {
  callbacks?: RestApiCallbacks<T>;
  headers?: Record<string, string>;
};

export function useRestApi() {
  const [loading, setLoading] = useState(false);

  const makeRequest = useCallback(
    async <T = any,>(
      endpoint: string,
      options: RequestInit = {},
      restOptions: RestApiOptions<T> = {},
    ): Promise<T> => {
      setLoading(true);

      try {
        const jwtToken = localStorage.getItem("jwtToken");
        const baseUrl = Values.URLS.REST.replace(/\/+$/, "");
        const normalizedEndpoint = endpoint.startsWith("/")
          ? endpoint
          : `/${endpoint}`;
        const url = `${baseUrl}${normalizedEndpoint}`;

        const response = await fetch(url, {
          ...options,
          headers: {
            "Content-Type": "application/json",
            ...(jwtToken ? { Authorization: `JWT ${jwtToken}` } : {}),
            ...restOptions.headers,
            ...options.headers,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage =
            errorData.detail || errorData.message || `HTTP ${response.status}`;
          throw new Error(errorMessage);
        }

        const contentType = response.headers.get("content-type");
        const data =
          contentType && contentType.includes("application/json")
            ? await response.json()
            : ({} as T);

        restOptions.callbacks?.onSuccess?.(data);

        return data;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "An unknown error occurred";

        restOptions.callbacks?.onError?.(errorMessage);

        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const get = useCallback(
    <T = any,>(endpoint: string, restOptions?: RestApiOptions<T>) =>
      makeRequest<T>(endpoint, { method: "GET" }, restOptions ?? {}),
    [makeRequest],
  );

  const post = useCallback(
    <T = any,>(
      endpoint: string,
      data?: any,
      restOptions: RestApiOptions<T> = {},
    ): Promise<T> =>
      makeRequest<T>(
        endpoint,
        {
          method: "POST",
          body: data ? JSON.stringify(data) : undefined,
        },
        restOptions,
      ),
    [makeRequest],
  );

  const put = useCallback(
    <T = any,>(
      endpoint: string,
      data?: any,
      restOptions: RestApiOptions<T> = {},
    ): Promise<T> =>
      makeRequest<T>(
        endpoint,
        {
          method: "PUT",
          body: data ? JSON.stringify(data) : undefined,
        },
        restOptions,
      ),
    [makeRequest],
  );

  const del = useCallback(
    <T = any,>(
      endpoint: string,
      restOptions: RestApiOptions<T> = {},
    ): Promise<T> =>
      makeRequest<T>(endpoint, { method: "DELETE" }, restOptions),
    [makeRequest],
  );

  return {
    loading,
    get,
    post,
    put,
    delete: del,
    makeRequest,
  };
}
